/**
 * Expense Category Editor
 * =======================
 * 
 * Allows users to view and correct AI-categorized expense line items.
 * Part of P2: P&L Edit UI feature.
 * 
 * Features:
 * - View parsed expenses grouped by category
 * - Change category assignments
 * - Track confidence scores
 * - Save corrections to case
 * - Trigger re-calculation with corrected data
 */

import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { pluralize } from '../utils/pluralize';
import { FinancialValue } from './shared/FinancialValue';

// Expense category definitions matching backend ExpenseCategory enum
const EXPENSE_CATEGORIES = [
  { value: 'officer_compensation', label: 'Officer Compensation', description: 'Owner/officer salary - often reduced during closure', color: 'var(--color-needs-human-tint)' },
  { value: 'salaried_wages', label: 'Salaried Wages', description: 'Management salaries - may be retained or laid off', color: 'var(--color-needs-human-tint)' },
  { value: 'hourly_wages', label: 'Hourly Wages', description: 'Line staff - usually 0% during extended closures', color: 'var(--color-needs-human-tint)' },
  { value: 'rent', label: 'Rent', description: 'Lease obligations - typically 100% continuation', color: 'transparent' },
  { value: 'utilities', label: 'Utilities', description: 'Minimal for preservation - 10-40%', color: 'transparent' },
  { value: 'insurance', label: 'Insurance', description: 'Reduced but needed - 60-100%', color: 'var(--color-needs-human-tint)' },
  { value: 'professional_services', label: 'Professional Services', description: 'Legal/accounting - INCREASES during claims', color: 'var(--color-needs-human-tint)' },
  { value: 'depreciation', label: 'Depreciation', description: 'Non-cash expense - accounting entry only', color: 'var(--color-bg-elevated)' },
  { value: 'debt_service', label: 'Debt Service', description: 'Loan payments - obligated, 100%', color: 'var(--color-needs-human-tint)' },
  { value: 'taxes_licenses', label: 'Taxes & Licenses', description: 'Property tax, licenses - obligated, 100%', color: 'var(--color-needs-human-tint)' },
  { value: 'other', label: 'Other', description: 'Discretionary, mostly eliminated (0-50%)', color: 'var(--color-bg-elevated)' },
];

interface ExpenseItem {
  id: string;
  original_label: string;
  amount: number;
  category: string;
  expense_category: string | null;
  confidence: number;
  period_label: string;
  period_start: string;
}

interface CategorySummary {
  category: string;
  total: number;
  item_count: number;
  avg_confidence: number;
}

interface ExpenseCategoryEditorProps {
  caseId: string;
  onSave?: (corrections: ExpenseCorrection[]) => void;
  onRecalculate?: () => void;
  readOnly?: boolean;
}

interface ExpenseCorrection {
  item_id: string;
  original_category: string;
  new_category: string;
  reason?: string;
}

export default function ExpenseCategoryEditor({
  caseId,
  onSave,
  onRecalculate,
  readOnly = false
}: ExpenseCategoryEditorProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<ExpenseItem[]>([]);
  const [corrections, setCorrections] = useState<Map<string, string>>(new Map());
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'amount' | 'confidence' | 'category'>('confidence');
  const [showLowConfidenceOnly, setShowLowConfidenceOnly] = useState(false);

  // Load expense data
  useEffect(() => {
    console.log('ExpenseCategoryEditor: Loading expenses for case:', caseId);
    loadExpenses();
  }, [caseId]);

  const loadExpenses = async () => {
    console.log('ExpenseCategoryEditor: loadExpenses started');
    setLoading(true);
    setError(null);
    try {
      console.log('ExpenseCategoryEditor: Calling API...');
      const response = await api.get<{success: boolean; expenses: ExpenseItem[]; total_count?: number; low_confidence_count?: number; message?: string}>(`/api/v1/document-pipeline/pl-expenses/${caseId}`);
      console.log('ExpenseCategoryEditor: API response:', response);
      if (response && response.success) {
        console.log('ExpenseCategoryEditor: Setting expenses:', response.expenses?.length);
        setExpenses(response.expenses || []);
      } else {
        console.log('ExpenseCategoryEditor: Response not successful:', response);
        setError(response?.message || 'Failed to load expenses');
      }
    } catch (err: any) {
      console.error('ExpenseCategoryEditor: Error loading expenses:', err);
      setError(err.message || 'Failed to load expense data');
    } finally {
      console.log('ExpenseCategoryEditor: Setting loading to false');
      setLoading(false);
    }
  };

  // Calculate category summaries
  const categorySummaries: CategorySummary[] = EXPENSE_CATEGORIES.map(cat => {
    const items = expenses.filter(e => 
      (corrections.get(e.id) || e.expense_category || e.category) === cat.value
    );
    return {
      category: cat.value,
      total: items.reduce((sum, e) => sum + e.amount, 0),
      item_count: items.length,
      avg_confidence: items.length > 0 
        ? items.reduce((sum, e) => sum + e.confidence, 0) / items.length 
        : 0
    };
  });

  // Filter and sort expenses
  const filteredExpenses = expenses
    .filter(e => {
      if (filter !== 'all' && (corrections.get(e.id) || e.expense_category || e.category) !== filter) {
        return false;
      }
      if (showLowConfidenceOnly && e.confidence >= 0.7) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'amount':
          return b.amount - a.amount;
        case 'confidence':
          return a.confidence - b.confidence; // Low confidence first
        case 'category':
          return (a.expense_category || '').localeCompare(b.expense_category || '');
        default:
          return 0;
      }
    });

  const handleCategoryChange = (itemId: string, newCategory: string) => {
    const newCorrections = new Map(corrections);
    const item = expenses.find(e => e.id === itemId);
    if (item && newCategory !== (item.expense_category || item.category)) {
      newCorrections.set(itemId, newCategory);
    } else {
      newCorrections.delete(itemId);
    }
    setCorrections(newCorrections);
  };

  const handleSave = async () => {
    if (corrections.size === 0) return;
    
    setSaving(true);
    try {
      const correctionsList: ExpenseCorrection[] = [];
      corrections.forEach((newCategory, itemId) => {
        const item = expenses.find(e => e.id === itemId);
        if (item) {
          correctionsList.push({
            item_id: itemId,
            original_category: item.expense_category || item.category,
            new_category: newCategory,
            reason: 'User correction'
          });
        }
      });

      const response = await api.post<{success: boolean; corrections_applied?: number; message?: string}>(`/api/v1/document-pipeline/pl-expenses/${caseId}/corrections`, {
        corrections: correctionsList
      });

      if (response.success) {
        setCorrections(new Map());
        loadExpenses(); // Reload with corrections applied
        onSave?.(correctionsList);
      } else {
        setError(response.message || 'Failed to save corrections');
      }
    } catch (err: any) {
      console.error('Error saving corrections:', err);
      setError(err.message || 'Failed to save corrections');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      // Recompute future-closure demand from the (now-edited) stored data. The real endpoint
      // is calculate/{report_id} (there is no recalculate route); it requires a body but reads
      // every field via parameters.get(...) with defaults, so {} recomputes from server config.
      await api.post(`/api/v1/future-closure/calculate/${caseId}`, {});
      onRecalculate?.();
    } catch (err: any) {
      console.error('Error triggering recalculation:', err);
      setError('Failed to trigger recalculation');
    }
  };

  const getConfidenceColor = (confidence: number): React.CSSProperties => {
    if (confidence >= 0.8) return { color: 'var(--color-verified)', backgroundColor: 'transparent' };
    if (confidence >= 0.5) return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
    return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
  };

  const getCategoryInfo = (value: string) => {
    return EXPENSE_CATEGORIES.find(c => c.value === value) || EXPENSE_CATEGORIES[EXPENSE_CATEGORIES.length - 1];
  };

  if (loading) {
    return (
      <div className="p-6 rounded-lg shadow" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-brand)' }}></div>
          <span className="ml-3" style={{ color: 'var(--color-text-secondary)' }}>Loading expense data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 rounded-lg shadow" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <div className="flex items-center">
            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20" style={{ color: 'var(--color-needs-human-text)' }}>
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span style={{ color: 'var(--color-needs-human-text)' }}>{error}</span>
          </div>
          <button
            onClick={loadExpenses}
            className="mt-3 px-4 py-2 text-sm rounded"
            style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (expenses.length === 0) {
    return (
      <div className="p-6 rounded-lg shadow" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="text-center py-10">
          <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--color-text-dim)' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>No P&L Data</h3>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Upload a P&L statement to view and edit expense categories.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg shadow" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium" style={{ color: 'var(--color-text-primary)' }}>Expense Category Editor</h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
              Review and correct AI-categorized expenses for accurate Future Closure calculations
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {corrections.size > 0 && (
              <span className="px-2 py-1 text-xs font-medium rounded" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                {corrections.size} pending changes
              </span>
            )}
            {!readOnly && corrections.size > 0 && (
              <>
                <button
                  onClick={() => setCorrections(new Map())}
                  className="px-3 py-2 text-sm"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Discard
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50"
                  style={{ color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' }}
                >
                  {saving ? 'Saving...' : 'Save Corrections'}
                </button>
              </>
            )}
            {!readOnly && (
              <button
                onClick={handleRecalculate}
                className="px-4 py-2 text-sm font-medium border rounded-md"
                style={{ color: 'var(--color-needs-human-text)', borderColor: 'var(--color-brand)' }}
              >
                Recalculate
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category Summary Cards */}
      <div className="px-6 py-4 border-b" style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border)' }}>
        <h4 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-secondary)' }}>Category Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {categorySummaries.filter(s => s.item_count > 0).map(summary => {
            const catInfo = getCategoryInfo(summary.category);
            return (
              <button
                key={summary.category}
                onClick={() => setFilter(filter === summary.category ? 'all' : summary.category)}
                className={`p-3 rounded-lg text-left transition-all ${
                  filter === summary.category ? 'shadow' : 'hover:shadow'
                }`}
                style={
                  filter === summary.category
                    ? { backgroundColor: 'var(--color-bg-surface)', boxShadow: '0 0 0 2px var(--color-brand)' }
                    : { backgroundColor: catInfo.color }
                }
              >
                <div className="text-xs font-medium truncate" style={{ color: 'var(--color-text-muted)' }}>{catInfo.label}</div>
                <div className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={summary.total} /></div>
                <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{summary.item_count} items</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showLowConfidenceOnly}
              onChange={(e) => setShowLowConfidenceOnly(e.target.checked)}
              className="rounded border focus:ring-2"
              style={{ borderColor: 'var(--color-border)', accentColor: 'var(--color-brand)' }}
            />
            <span style={{ color: 'var(--color-text-secondary)' }}>Show low confidence only</span>
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="text-sm border rounded-md focus:ring-2"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
          >
            <option value="confidence">Sort by Confidence</option>
            <option value="amount">Sort by Amount</option>
            <option value="category">Sort by Category</option>
          </select>
        </div>
        <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          Showing {filteredExpenses.length} of {pluralize(expenses.length, 'item')}
        </div>
      </div>

      {/* Expense Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[color:var(--color-border-faint)]">
          <thead style={{ backgroundColor: 'var(--color-bg-base)' }}>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-label)' }}>
                Description
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-label)' }}>
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-label)' }}>
                Period
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-label)' }}>
                Confidence
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-label)' }}>
                Category
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--color-border-faint)]" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            {filteredExpenses.map((item) => {
              const currentCategory = corrections.get(item.id) || item.expense_category || item.category;
              const isModified = corrections.has(item.id);
              const catInfo = getCategoryInfo(currentCategory);
              
              return (
                <tr key={item.id} style={isModified ? { backgroundColor: 'var(--color-needs-human-tint)' } : undefined}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium truncate max-w-xs" title={item.original_label} style={{ color: 'var(--color-text-primary)' }}>
                      {item.original_label}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      <FinancialValue value={item.amount} cents />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      {item.period_label || item.period_start}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={getConfidenceColor(item.confidence)}>
                      {Math.round(item.confidence * 100)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {readOnly ? (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: catInfo.color }}>
                        {catInfo.label}
                      </span>
                    ) : (
                      <select
                        value={currentCategory}
                        onChange={(e) => handleCategoryChange(item.id, e.target.value)}
                        className="text-sm border rounded-md focus:ring-2"
                        style={isModified
                          ? { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', boxShadow: '0 0 0 2px var(--color-brand)' }
                          : { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }
                        }
                      >
                        {EXPENSE_CATEGORIES.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer with Legend */}
      <div className="px-6 py-4 border-t" style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border)' }}>
        <h4 className="text-xs font-medium uppercase mb-2" style={{ color: 'var(--color-text-label)' }}>Category Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
          {EXPENSE_CATEGORIES.slice(0, 8).map(cat => (
            <div key={cat.value} className="flex items-start space-x-2">
              <div className="w-3 h-3 rounded mt-0.5" style={{ backgroundColor: cat.color }}></div>
              <div>
                <span className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>{cat.label}</span>
                <span className="ml-1" style={{ color: 'var(--color-text-muted)' }}>- {cat.description}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
